import { useMutation, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import React from 'react';
import { useForm } from 'react-hook-form';

import useLinkProps from '../../../hooks/use-link-props';
import { fastRegisterSave } from '../../../services/auth/signup';
import { useAddAlert, useRemoveAlert } from '../../modules/alerts';
import FeaturedLink from '../../ui/links/featured';

import SignUpFormContent from './form-content';
import SocialLoginButtons from '../../modules/social-login';

const SignUpForm = () => {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { locale = 'en', push } = useRouter();
  const getLink = useLinkProps();
  const addAlert = useAddAlert();
  const removeAlert = useRemoveAlert();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange' });

  const { mutate, isLoading } = useMutation(fastRegisterSave, {
    onSuccess(data) {
      client.removeQueries('user-dependent');
      removeAlert('signup');

      window.dataLayer.push({
        event: 'userSignup',
        userID: data.user_id, // user id
      });

      const link = getLink('private-area', undefined);
      push(link.href, link.as);
    },
    onError(error: Record<string, any>) {
      let message;
      if (error.serverError) {
        message = t('forms:errors.serverError');
      } else if (error.securityError) {
        message = t('signup:securityError');
      } else {
        message = t('signup:error');
      }
      addAlert({
        id: 'signup',
        text: message,
        items: error.non_field_errors,
        type: 'DANGER',
      });
      Object.keys(error).map(function (key, i) {
        if (
          key !== 'non_field_errors' &&
          key !== 'serverError' &&
          key !== 'securityError'
        ) {
          setError(key, {
            type: 'manual',
            message: error[key],
          });
        }
      });
    },
  });

  const onSubmit = useCallback(
    (formData) => {
      formData.language = locale;
      formData.shipping_destination = Cookies.get('_shipping_location');
      formData.currency = Cookies.get('_currency');
      mutate({
        locale: locale,
        formData,
        csrftoken: Cookies.get('csrftoken') || '',
      });
    },
    [mutate, locale]
  );

  const termsLink = (
    <Link {...getLink('terms-and-conditions', undefined)} passHref legacyBehavior>
      <FeaturedLink
        variant="neutral"
        className="font-bold text-neutral-80"
        target="_blank"
      >
        {t('signup:terms-link')}
      </FeaturedLink>
    </Link>
  );

  const privacyLink = (
    <Link
      {...getLink('terms-and-conditions', undefined, t('signup:privacy-hash'))}
      passHref
      legacyBehavior>
      <FeaturedLink
        variant="neutral"
        className="font-bold text-neutral-80"
        target="_blank"
      >
        {t('signup:privacy-link')}
      </FeaturedLink>
    </Link>
  );
  const cookiesLink = (
    <Link
      {...getLink('terms-and-conditions', undefined, t('signup:cookies-hash'))}
      passHref
      legacyBehavior>
      <FeaturedLink
        variant="neutral"
        className="font-bold text-neutral-80"
        target="_blank"
      >
        {t('signup:cookies-link')}
      </FeaturedLink>
    </Link>
  );

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="pt-6 flex flex-col gap-4 w-full lg:w-full"
      >
        <SignUpFormContent
          register={register}
          errors={errors}
          disabled={isLoading}
          isValid={isValid && Object.keys(errors).length == 0 && !isLoading}
        />
        <div className="w-full">
          <SocialLoginButtons isSignUp />
        </div>
        <p className="text-sm">
          <Trans
            i18nKey="signup:agree-terms"
            components={{
              b: <b />,
              termsLink: termsLink,
              privacyLink: privacyLink,
              cookiesLink: cookiesLink,
            }}
          />
        </p>
      </form>
    </>
  );
};
export default SignUpForm;
